import React from "react";
import { Ico } from "./Icon";

interface PaginationProps {
  currentPage: number;
  lastPage: number;
  total: number;
  from: number;
  to: number;
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  perPage?: number;
  loading?: boolean;
}

const PER_PAGE_OPTIONS = [15, 20, 50, 100];

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  lastPage,
  total,
  from,
  to,
  onPageChange,
  onPerPageChange,
  perPage = 20,
  loading = false,
}) => {
  // Build page number array with ellipsis
  const pages: (number | "...")[] = [];
  const delta = 2;
  for (let i = 1; i <= lastPage; i++) {
    if (
      i === 1 ||
      i === lastPage ||
      (i >= currentPage - delta && i <= currentPage + delta)
    ) {
      pages.push(i);
    } else if (i === currentPage - delta - 1 || i === currentPage + delta + 1) {
      pages.push("...");
    }
  }

  const btn = (
    label: React.ReactNode,
    page: number,
    disabled: boolean,
    active = false,
  ) => (
    <button
      key={String(page) + String(label)}
      onClick={() => !disabled && onPageChange(page)}
      disabled={disabled || loading}
      className={[
        "min-w-[32px] h-8 px-2 rounded-lg text-xs font-semibold transition-all",
        "flex items-center justify-center",
        active
          ? "bg-primary text-[var(--on-primary)] font-bold"
          : disabled
            ? "text-gray-700 cursor-not-allowed"
            : "bg-gray-800/60 text-gray-400 hover:bg-gray-700/60 hover:text-gray-50",
        loading ? "opacity-50" : "",
      ].join(" ")}
    >
      {label}
    </button>
  );

  // Hide entirely if only 1 page AND no perPage selector
  if (lastPage <= 1 && !onPerPageChange) return null;

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-800/50 gap-3 flex-wrap">
      {/* Left: info + per-page selector */}
      <div className="flex items-center gap-3">
        <p className="text-[11px] text-gray-500">
          {total > 0 ? (
            <>
              Showing{" "}
              <span className="text-gray-300 font-mono">
                {from}–{to}
              </span>{" "}
              of <span className="text-gray-300 font-mono">{total}</span>
            </>
          ) : (
            <span className="text-gray-600">No results</span>
          )}
        </p>

        {/* Per-page dropdown */}
        {onPerPageChange && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-gray-600 hidden sm:block">
              per page
            </span>
            <div className="flex gap-0.5 bg-gray-900/60 border border-gray-800/60 rounded-lg p-0.5">
              {PER_PAGE_OPTIONS.map((n) => (
                <button
                  key={n}
                  onClick={() => {
                    onPerPageChange(n);
                    onPageChange(1);
                  }}
                  disabled={loading}
                  className={`px-2 py-1 rounded-md text-[10px] font-bold transition-all ${
                    perPage === n
                      ? "bg-primary text-[var(--on-primary)]"
                      : "text-gray-500 hover:text-gray-300"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right: page buttons — hide if only 1 page */}
      {lastPage > 1 && (
        <div className="flex items-center gap-1">
          {btn(
            <Ico name="arrow_left" className="w-3.5 h-3.5" />,
            currentPage - 1,
            currentPage === 1,
          )}
          {pages.map((p, i) =>
            p === "..." ? (
              <span
                key={`ellipsis-${i}`}
                className="px-1 text-gray-600 text-xs"
              >
                …
              </span>
            ) : (
              btn(p, p, false, p === currentPage)
            ),
          )}
          {btn(
            <Ico name="arrow_right" className="w-3.5 h-3.5" />,
            currentPage + 1,
            currentPage === lastPage,
          )}
        </div>
      )}
    </div>
  );
};

export default Pagination;
